/**
 * Chord symbols, as MusicXML says them.
 *
 * The document's kind names are MusicXML's own -- `major-seventh`, `half-diminished` --
 * so nothing is translated here, which is why they were chosen. What has to be decided is
 * *where* a symbol goes, because MusicXML has no bar-local position for one.
 *
 * A `<harmony>` stands at the cursor's position in the bar, and the cursor is moved only by
 * notes. A chord symbol lands on a beat that may be in the middle of a long note, so it is
 * written before the last note that began at or before it, with an `<offset>` carrying the
 * rest of the way. That is the shape readers expect and the one Verovio draws from.
 *
 * It is written once per bar, from the first voice's timeline, because the cursor is the
 * bar's rather than a voice's and a symbol repeated per voice would be drawn twice.
 */

import { occupiedTicks } from "../document/query";
import { suffixOf } from "../document/harmony";
import type { ChordRoot, Harmony } from "../document/harmony";
import type { EventId } from "../document/ids";
import type { PartMeasure, Voice } from "../document/types";
import { el, leaf } from "../xml/write";
import type { XmlNode } from "../xml/write";
import { toDivisions } from "./common";

/** Where each bar's chord symbols go: keyed by the note they are written before. */
export interface HarmonyPlan {
  readonly byEvent: ReadonlyMap<EventId, readonly XmlNode[]>;
  /** Symbols with no note at or before them, which go at the head of the bar. */
  readonly leading: readonly XmlNode[];
}

const EMPTY: HarmonyPlan = { byEvent: new Map(), leading: [] };

function rootNodes(prefix: "root" | "bass", root: ChordRoot): readonly XmlNode[] {
  return [
    leaf(`${prefix}-step`, root.step),
    // A natural root writes no alter at all: `<root-alter>0</root-alter>` is legal but
    // makes some readers print a courtesy natural beside the letter.
    root.alter === 0 ? null : leaf(`${prefix}-alter`, root.alter),
  ].filter((node): node is XmlNode => node !== null);
}

/**
 * One `<harmony>`, offset from the note it is written before.
 *
 * `text` on `<kind>` is what a reader prints when it has no engraving of its own for the
 * kind, and giving it keeps `Cmaj7` from being drawn as `C major-seventh` or as a bare `C`.
 */
export function harmonyNode(harmony: Harmony, offset: number, divisions: number): XmlNode {
  return el("harmony", {}, [
    el("root", {}, rootNodes("root", harmony.root)),
    leaf("kind", harmony.kind, { text: suffixText(harmony) }),
    harmony.bass === null ? null : el("bass", {}, rootNodes("bass", harmony.bass)),
    offset === 0 ? null : leaf("offset", toDivisions(offset, divisions)),
  ]);
}

/** What is printed after the letter, kept out of the attribute when there is nothing. */
function suffixText(harmony: Harmony): string | null {
  const text = suffixOf(harmony.kind);
  return text.length === 0 ? null : text;
}

/**
 * Work out where each of a bar's symbols is written.
 *
 * `timeline` is the voice the exporter writes first, which is the one the cursor is on
 * when the symbols are emitted. It is not always the bar's first voice: a bar offering an
 * ossia writes that above the staff it stands over, and so writes it first.
 */
export function harmonyPlan(
  measure: PartMeasure,
  timeline: Voice | null,
  divisions: number,
  capacity: number,
): HarmonyPlan {
  if (measure.harmonies.length === 0) return EMPTY;
  const voice = timeline;
  if (voice === null) return EMPTY;

  const starts: { readonly id: EventId; readonly onset: number }[] = [];
  let onset = 0;
  for (const event of voice.events) {
    starts.push({ id: event.id, onset });
    onset += occupiedTicks(event, capacity);
  }

  const byEvent = new Map<EventId, XmlNode[]>();
  const leading: XmlNode[] = [];
  for (const harmony of [...measure.harmonies].sort((a, b) => a.onset - b.onset)) {
    let host: (typeof starts)[number] | undefined;
    for (const start of starts) {
      if (start.onset > harmony.onset) break;
      host = start;
    }
    if (host === undefined) {
      leading.push(harmonyNode(harmony, harmony.onset, divisions));
      continue;
    }
    const node = harmonyNode(harmony, harmony.onset - host.onset, divisions);
    const already = byEvent.get(host.id);
    if (already === undefined) byEvent.set(host.id, [node]);
    else already.push(node);
  }
  return { byEvent, leading };
}
